import React, { useEffect, useState } from "react";
import axios from "axios";
import HiddenVAKReport from "./HiddenVAKReport";
import SurveyInviteWidget from "./SurveyInviteWidget";

// ── VAK style → colour map (sourced from HiddenVAKReport.jsx) ─────────────────
// Visual  : #1e3a8a dark-blue  (bar fill: #1e3a8a → #3b82f6)
// Auditory: #7f1d1d dark-red   (bar fill: #7f1d1d → #ef4444)
// Kinestht: #b45309 amber      (bar fill: #b45309 → #f59e0b)



// ── DominantStylePill ──────────────────────────────────────────────────────────
// Single dominant  → solid bg / border / text identical to the individual score pills
// Multi  dominant  → gradient of the light bg shades + averaged border + averaged text colour
//
// All 7 possible combinations are handled dynamically:
//   Visual | Auditory | Kinesthetic
//   Visual/Auditory | Visual/Kinesthetic | Auditory/Kinesthetic
//   Visual/Auditory/Kinesthetic
const PILL_TOKENS = {
  visual:      { bg: "#f0f4ff", color: "#3b5ec0", border: "#bfdbfe" },
  auditory:    { bg: "#fef2f2", color: "#b93030", border: "#fecaca" },
  kinesthetic: { bg: "#fffbeb", color: "#d9790a", border: "#fde68a" },
};

const hexToRgb = (h) => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
const rgbToHex = ([r, g, b]) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
const avgHex = (hexes) => {
  const channels = hexes.map(hexToRgb);
  return rgbToHex([0, 1, 2].map((i) => channels.reduce((s, c) => s + c[i], 0) / channels.length));
};

const DominantStylePill = ({ dominantStyle }) => {
  if (!dominantStyle) return <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>;

  const parts = dominantStyle
    .split("/")
    .map((s) => s.trim().toLowerCase())
    .filter((s) => PILL_TOKENS[s]);

  const pillBase = {
    padding:      "4px 12px",
    borderRadius: "999px",
    fontSize:     "12px",
    fontWeight:   "800",
    whiteSpace:   "nowrap",
    display:      "inline-block",
    width:        "max-content",
    lineHeight:   "1.6",
  };

  // ── Single dominant: exact same solid colours as the individual score pills ──
  if (parts.length <= 1) {
    const key = parts[0] || "";
    const c   = PILL_TOKENS[key] || { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db" };
    return (
      <span style={{ ...pillBase, background: c.bg, color: c.color, border: `1px solid ${c.border}` }}>
        {dominantStyle}
      </span>
    );
  }

  // ── Multi-dominant ──────────────────────────────────────────────────────────
  // Background: gradient across the light bg shades of each style
  const bgStops = parts.map((p) => PILL_TOKENS[p].bg);
  const step    = 100 / (bgStops.length - 1);
  const bgGradient = `linear-gradient(135deg, ${bgStops.map((c, i) => `${c} ${Math.round(i * step)}%`).join(", ")})`;

  // Border & text: averaged hex of the constituent style tokens
  const blendedBorder = avgHex(parts.map((p) => PILL_TOKENS[p].border));
  const blendedColor  = avgHex(parts.map((p) => PILL_TOKENS[p].color));

  return (
    <span style={{
      ...pillBase,
      background: bgGradient,
      border:     `1px solid ${blendedBorder}`,
      color:      blendedColor,
    }}>
      {dominantStyle}
    </span>
  );
};

// ── Per-dimension colour (for score pills) — alias of PILL_TOKENS ────────────
const VAK_COLORS = PILL_TOKENS;

export default function GlobalVAKResponses({ surveyId }) {
  const [vakData,        setVakData]        = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Filters ──
  const [filterName,      setFilterName]      = useState("");
  const [filterEmail,     setFilterEmail]     = useState("");
  const [filterStyle,     setFilterStyle]     = useState("");
  const [filterCompany,   setFilterCompany]   = useState("");
  const [filterDept,      setFilterDept]      = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate,   setFilterEndDate]   = useState("");

  // ── PDF state ──
  const [downloadData,    setDownloadData]    = useState(null);
  const [isGeneratingRow, setIsGeneratingRow] = useState(null);

  // ================= FETCH =================
  useEffect(() => {
    if (surveyId) fetchVAKResults();
  }, [surveyId]);

  const fetchVAKResults = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/response/calculate-score-vak-all`,
        { survey_id: surveyId }
      );
      setVakData(res.data.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch VAK results", err);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  // Collect unique dropdown options
  const uniqueStyles = [
    ...new Set(vakData.map(u => (u.dominantStyle || "")).filter(Boolean)),
  ].sort();

  const uniqueCompanies = [
    ...new Set(vakData.map(u => (u.userInfo?.company || "").trim()).filter(Boolean)),
  ].sort();

  const uniqueDepts = [
    ...new Set(vakData.map(u => (u.userInfo?.department || "").trim()).filter(Boolean)),
  ].sort();

  const filteredData = vakData.filter((user) => {
    const name    = (user.userInfo?.name       || "").toLowerCase();
    const email   = (user.userInfo?.email      || "").toLowerCase();
    const company = (user.userInfo?.company    || "").trim().toLowerCase();
    const dept    = (user.userInfo?.department || "").trim().toLowerCase();

    const matchName    = name.includes(filterName.toLowerCase());
    const matchEmail   = email.includes(filterEmail.toLowerCase());
    const matchCompany = filterCompany === "" || company === filterCompany.toLowerCase();
    const matchDept    = filterDept    === "" || dept    === filterDept.toLowerCase();
    const matchStyle   =
      filterStyle === "" ||
      (user.dominantStyle || "").toLowerCase() === filterStyle.toLowerCase();

    let matchDate = true;
    if (filterStartDate || filterEndDate) {
      const submissionDate = new Date(user.submission_time || user.userInfo?.submission_time);
      submissionDate.setHours(0, 0, 0, 0);
      if (filterStartDate) {
        const start = new Date(filterStartDate);
        start.setHours(0, 0, 0, 0);
        if (submissionDate < start) matchDate = false;
      }
      if (filterEndDate) {
        const end = new Date(filterEndDate);
        end.setHours(23, 59, 59, 999);
        if (submissionDate > end) matchDate = false;
      }
    }

    return matchName && matchEmail && matchCompany && matchDept && matchStyle && matchDate;
  });

  const clearFilters = () => {
    setFilterName("");
    setFilterEmail("");
    setFilterStyle("");
    setFilterCompany("");
    setFilterDept("");
    setFilterStartDate("");
    setFilterEndDate("");
  };

  // ── Summary counts — preserve original dominantStyle casing for display ──
  const styleCounts = vakData.reduce((acc, u) => {
    const key = u.dominantStyle || "";
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  // ================= PDF DOWNLOAD =================
  const handleDownloadPDF = (user, rowIndex) => {
    setIsGeneratingRow(rowIndex);

    const formattedData = {
      userInfo: {
        name:       user.userInfo?.name       || "Participant",
        email:      user.userInfo?.email      || "",
        phone:      user.userInfo?.phone      || "",
        company:    user.userInfo?.company    || "",
        department: user.userInfo?.department || "",
        level:      user.userInfo?.level      || "",
        location:   user.userInfo?.location   || "",
      },
      scores: {
        Visual:      user.scores?.Visual      || 0,
        Auditory:    user.scores?.Auditory    || 0,
        Kinesthetic: user.scores?.Kinesthetic || 0,
      },
      dominantStyle:   user.dominantStyle || "N/A",
      submission_time: user.submission_time || user.userInfo?.submission_time,
    };

    setDownloadData(formattedData);
  };

  const handleDownloadComplete = () => {
    setDownloadData(null);
    setIsGeneratingRow(null);
  };

  // ================= UI =================
  if (loading) return <div className="loading-state">Loading VAK data...</div>;

  return (
    <div style={{ marginTop: "20px" }}>

       <SurveyInviteWidget surveyId={surveyId} />

      {/* Hidden PDF renderer — keyed so a fresh instance (fresh DOM, fresh
          font <link>) mounts for every download, instead of reusing one
          permanently-mounted instance across clicks. This mirrors how
          VakSurvey.jsx already works correctly. */}
      <HiddenVAKReport
        key={isGeneratingRow}
        participantData={downloadData}
        onComplete={handleDownloadComplete}
      />

      {/* ── Header ── */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "center", marginBottom: "15px",
      }}>
        <h3 style={{ margin: 0 }}>🧠 VAK Learning Styles ({filteredData.length})</h3>
        <span style={{
          background: "#0f2744", color: "white",
          padding: "5px 10px", borderRadius: "4px",
          fontSize: "12px", fontWeight: "bold",
        }}>
          Global Public View
        </span>
      </div>

      {/* ── Dominant-style summary pills ── */}
      {vakData.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
          {Object.entries(styleCounts)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([style, count]) => {
              const parts = style
                .split("/")
                .map((s) => s.trim().toLowerCase())
                .filter((s) => PILL_TOKENS[s]);

              const isActive = filterStyle.toLowerCase() === style.toLowerCase();

              // ── Derive pill colours ──────────────────────────────────────
              let bgStyle, borderColor, textColor;

              if (parts.length === 0) {
                // unknown/fallback
                bgStyle     = "#f3f4f6";
                borderColor = "#d1d5db";
                textColor   = "#6b7280";
              } else if (parts.length === 1) {
                const c = PILL_TOKENS[parts[0]];
                bgStyle     = c.bg;
                borderColor = c.border;
                textColor   = c.color;
              } else {
                // multi-dominant: gradient bg + averaged border & text
                const bgStops = parts.map((p) => PILL_TOKENS[p].bg);
                const step    = 100 / (bgStops.length - 1);
                bgStyle = `linear-gradient(135deg, ${bgStops.map((c, i) => `${c} ${Math.round(i * step)}%`).join(", ")})`;
                borderColor = avgHex(parts.map((p) => PILL_TOKENS[p].border));
                textColor   = avgHex(parts.map((p) => PILL_TOKENS[p].color));
              }

              return (
                <span
                  key={style}
                  onClick={() => setFilterStyle(isActive ? "" : style)}
                  title={`Click to filter by ${style}`}
                  style={{
                    background:    bgStyle,
                    color:         textColor,
                    border:        `1px solid ${borderColor}`,
                    padding:       "4px 12px",
                    borderRadius:  "999px",
                    fontSize:      "12px",
                    fontWeight:    "700",
                    cursor:        "pointer",
                    display:       "inline-block",
                    width:         "max-content",
                    whiteSpace:    "nowrap",
                    outline:       isActive ? `2px solid ${textColor}` : "none",
                    outlineOffset: "2px",
                    transition:    "outline 0.15s",
                  }}
                >
                  {style}: {count}
                </span>
              );
            })}
          <span style={{ fontSize: "12px", color: "#888", alignSelf: "center", marginLeft: "4px" }}>
            ← click to filter
          </span>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "10px",
        background: "#f8f9fa", padding: "15px", borderRadius: "8px",
        border: "1px solid #ddd", marginBottom: "20px", alignItems: "center",
      }}>
        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Name</label>
          <input
            type="text"
            placeholder="Search name..."
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "150px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Email</label>
          <input
            type="text"
            placeholder="Search email..."
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Company</label>
          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Companies</option>
            {uniqueCompanies.map(c => (
              <option key={c} value={c.toLowerCase()}>{c}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Department</label>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Departments</option>
            {uniqueDepts.map(d => (
              <option key={d} value={d.toLowerCase()}>{d}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "160px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Dominant Style</label>
          <select
            value={filterStyle}
            onChange={(e) => setFilterStyle(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc", background: "#fff" }}
          >
            <option value="">All Styles</option>
            {uniqueStyles.map(s => (
              <option key={s} value={s.toLowerCase()}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>Start Date</label>
          <input
            type="date"
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ flex: "1", minWidth: "130px" }}>
          <label style={{ fontSize: "12px", fontWeight: "bold", color: "#555", display: "block", marginBottom: "4px" }}>End Date</label>
          <input
            type="date"
            value={filterEndDate}
            onChange={(e) => setFilterEndDate(e.target.value)}
            style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
        </div>

        <div style={{ marginTop: "20px" }}>
          <button
            onClick={clearFilters}
            style={{
              padding: "8px 15px", background: "#A81026", color: "white",
              border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold",
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {vakData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No VAK responses found.
        </div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#888" }}>
          No responses match your current filters.
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead>
              <tr style={{ background: "#f8f9fa", borderBottom: "2px solid #ddd" }}>
                <th style={{ padding: "12px" }}>Date</th>
                <th style={{ padding: "12px" }}>Participant</th>
                <th style={{ padding: "12px" }}>Company / Dept</th>
                <th style={{ padding: "12px", minWidth: "200px" }}>Scores</th>
                <th style={{ padding: "12px", minWidth: "160px", background: "#fdf6e3" }}>
                  🧠 VAK Results
                </th>
                <th style={{ padding: "12px", textAlign: "center" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((user, index) => {
                const v     = user.scores?.Visual      || 0;
                const a     = user.scores?.Auditory    || 0;
                const k     = user.scores?.Kinesthetic || 0;
                const isGenerating = isGeneratingRow === index;

                return (
                  <tr key={index} style={{ borderBottom: "1px solid #eee" }}>

                    {/* ── Date ── */}
                    <td style={{ padding: "12px", whiteSpace: "nowrap" }}>
                      {user.submission_time || user.userInfo?.submission_time
                        ? <>
                            {new Date(user.submission_time || user.userInfo?.submission_time).toLocaleDateString("en-US", {
                              month: "short", day: "numeric", year: "numeric",
                            })}<br />
                            <span style={{ fontSize: "12px", color: "#888" }}>
                              {new Date(user.submission_time || user.userInfo?.submission_time).toLocaleTimeString("en-US", {
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </span>
                          </>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
                      }
                    </td>

                    {/* ── Participant ── */}
                    <td style={{ padding: "12px" }}>
                      <b>{user.userInfo?.name || "Unknown"}</b><br />
                      <span style={{ fontSize: "13px", color: "#555" }}>
                        {user.userInfo?.email}<br />
                        {user.userInfo?.phone || ""}
                      </span>
                    </td>

                    {/* ── Company / Dept ── */}
                    <td style={{ padding: "12px" }}>
                      {user.userInfo?.company
                        ? <><b>{user.userInfo.company}</b><br /></>
                        : <span style={{ color: "#aaa", fontStyle: "italic" }}>N/A</span>
                      }
                      {user.userInfo?.department
                        ? <span style={{ fontSize: "13px", color: "#555" }}>{user.userInfo.department}</span>
                        : null
                      }
                    </td>

                    {/* ── Scores ── */}
                    <td style={{ padding: "12px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {[
                          { label: "Visual",      val: v, c: VAK_COLORS.visual      },
                          { label: "Auditory",    val: a, c: VAK_COLORS.auditory    },
                          { label: "Kinesthetic", val: k, c: VAK_COLORS.kinesthetic },
                        ].map(({ label, val, c }) => (
                          <span
                            key={label}
                            style={{
                              background:   c.bg,
                              color:        c.color,
                              border:       `1px solid ${c.border}`,
                              padding:      "2px 8px",
                              borderRadius: "4px",
                              fontSize:     "12px",
                              fontWeight:   "700",
                              whiteSpace:   "nowrap",
                            }}
                          >
                            {label}: {val}
                          </span>
                        ))}
                      </div>
                    </td>

                    {/* ── VAK Results — gradient pill for multi-dominant ── */}
                    <td style={{ padding: "12px", background: "#fffdf7" }}>
                      <DominantStylePill dominantStyle={user.dominantStyle} />
                    </td>

                    {/* ── Actions ── */}
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <button
                        onClick={() => handleDownloadPDF(user, index)}
                        disabled={isGenerating }
                        style={{
                          background:   isGenerating ? "#95a5a6" : "#A81026",
                          color:        "white",
                          border:       "none",
                          padding:      "8px 12px",
                          borderRadius: "6px",
                          cursor:       isGenerating ? "not-allowed" : "pointer",
                          fontWeight:   "bold",
                          fontSize:     "12px",
                          display:      "flex",
                          alignItems:   "center",
                          gap:          "5px",
                          margin:       "0 auto",
                          transition:   "background 0.3s",
                          whiteSpace:   "nowrap",
                        }}
                      >
                        {isGenerating ? "⏳ Generating..." : "📄 Download"}
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}